import React, { useState } from "react";
import SignatureUploadWidget from "./utils/SignatureUploadWidget";
import { useDataStore } from "@store/user_org";
import { useNotification } from "../../../context/NotificationContext";
import { FieldData } from "./utils/DraggableResizableField";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

// Configure PDF.js worker - using unpkg.com with .mjs for ES modules support
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface FillSignatureProps {
  pdfUrl: string;
  initialFields?: FieldData[];
  onClose: () => void;
  onSign: (fields: FieldData[], dimensions: { width: number; height: number }) => Promise<void>;
}

/**
 * FillSignature Component
 *
 * Displays a PDF for the user to sign. No fields are shown - the PDF is already prepared.
 * User only uploads/updates their signature and clicks "Signer".
 *
 * Usage:
 * ```tsx
 * <FillSignature
 *   pdfUrl={preparedPdfUrl}
 *   onClose={() => setOpen(false)}
 *   onSign={async (fields, dimensions) => {
 *     const response = await signAPI(preparedPdfUrl, fields, dimensions);
 *     setSignedPdfUrl(response.pdf_url);
 *   }}
 * />
 * ```
 */
const FillSignature: React.FC<FillSignatureProps> = ({
  pdfUrl,
  initialFields = [],
  onClose,
  onSign,
}) => {
  const userData = useDataStore((state) => state.userData);
  const { showNotification } = useNotification();
  const [loadingData, setLoadingData] = useState(false);
  const [numPages, setNumPages] = useState<number>(0);
  const [pdfDimensions, setPdfDimensions] = useState({ width: 1190.56, height: 1683.78 });
  const [pageWidth, setPageWidth] = useState(800);

  const handleSign = async () => {
    if (!userData?.infos?.signature_id) {
      showNotification({ message: "Veuillez télécharger votre signature avant de signer", type: "error" });
      return;
    }

    setLoadingData(true);
    try {
      const fields = initialFields.map((field) => ({
        ...field,
        type: field.type === "Texte" ? "Text" : field.type,
        content: field.type === "Signature" ? userData.infos.signature_id : field.content,
      }));

      await onSign(fields, pdfDimensions);
    } catch (error) {
      console.error("Error signing document:", error);
      showNotification({ message: "Échec de la signature du document", type: "error" });
    } finally {
      setLoadingData(false);
    }
  };

  const onDocumentLoadSuccess = async ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);

    // Get actual PDF dimensions with scale 2 (matching Hexasign)
    try {
      const loadingTask = pdfjs.getDocument(pdfUrl);
      const pdf = await loadingTask.promise;
      const firstPage = await pdf.getPage(1);
      const viewport = firstPage.getViewport({ scale: 2 });
      setPdfDimensions({ width: viewport.width, height: viewport.height });
      setPageWidth(viewport.width);
    } catch (error) {
      console.error("Error getting PDF dimensions:", error);
    }
  };

  return (
    <div className="h-full w-full rounded-lg bg-white overflow-hidden">
      <div className="flex h-full w-full">
        {/* Sidebar for signature upload */}
        <div className="w-96 p-6 h-full overflow-auto bg-white border-r border-gray-200 flex-shrink-0">
          <div className="flex flex-col gap-4 h-full">
            <div className="text-lg font-semibold">Signer le document</div>
            <div className="text-sm text-gray-600">
              Votre signature sera automatiquement ajoutée au document.
            </div>

            {/* Signature upload widget */}
            <SignatureUploadWidget />

            <div className="text-sm text-gray-600 mt-4">
              <strong>Instructions:</strong>
              <ul className="list-disc ml-5 mt-2 space-y-1">
                <li>Téléchargez votre signature ci-dessus</li>
                <li>Vérifiez le document PDF</li>
                <li>Cliquez sur "Signer" pour finaliser</li>
              </ul>
            </div>

            <div className="flex flex-col gap-3 mt-auto">
              <button
                type="button"
                className="w-full px-6 py-3 bg-gray-200 text-black rounded-lg hover:bg-gray-300 text-base"
                onClick={onClose}
              >
                Annuler
              </button>
              <button
                type="button"
                className="w-full px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed text-base"
                onClick={handleSign}
                disabled={loadingData || !userData?.infos?.signature_id}
              >
                {loadingData ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Signature en cours...
                  </div>
                ) : (
                  "Signer"
                )}
              </button>
            </div>
          </div>
        </div>

        {/* PDF Viewer */}
        <div
          style={{
            flex: 1,
            overflow: "auto",
            position: "relative",
            width: `${pageWidth}px`,
            margin: "0 auto",
            backgroundColor: "#f0f0f0",
          }}
        >
          <div style={{ position: "relative" }}>
            <Document
              file={pdfUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={(error) => {
                console.error("Error loading PDF:", error);
                showNotification({ message: "Erreur lors du chargement du PDF", type: "error" });
              }}
            >
              {Array.from(new Array(numPages), (_, index) => (
                <div
                  key={`page_${index + 1}`}
                  style={{
                    position: "relative",
                    width: `${pageWidth}px`,
                    marginBottom: "10px"
                  }}
                >
                  <Page
                    pageNumber={index + 1}
                    width={pageWidth}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                </div>
              ))}
            </Document>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FillSignature;
